import { ColumnDef, flexRender, Table as ReactTable, Row } from '@tanstack/react-table';
import { ChevronDownIcon, ColumnsIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  className?: string;
  table: ReactTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  getIsRowOpen?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;
  emptyMessage?: React.ReactNode;
  rowClassName?: string;
}

export function DataTable<TData, TValue>({
  className,
  table,
  columns,
  onRowClick,
  getIsRowOpen,
  emptyMessage = 'No results.',
  rowClassName,
}: DataTableProps<TData, TValue>) {
  return (
    <Table className={className}>
      <TableHeader className='bg-muted/50'>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className='h-12'>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table
            .getRowModel()
            .rows.map((row) => (
              <DataTableRow
                className={rowClassName || ''}
                key={row.id}
                row={row}
                onRowClick={onRowClick}
                getIsRowOpen={getIsRowOpen}
              />
            ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className='h-24 text-center'>
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

interface DataTableRowProps<TData> {
  row: Row<TData>;
  onRowClick?: (row: Row<TData>) => void;
  getIsRowOpen?: (row: Row<TData>) => boolean;
  className?: string;
}

function DataTableRow<TData>({ row, onRowClick, getIsRowOpen, className }: DataTableRowProps<TData>) {
  const isSelected = row.getIsSelected() || getIsRowOpen?.(row);
  return (
    <TableRow
      key={row.id}
      data-state={isSelected && 'selected'}
      onClick={() => onRowClick?.(row)}
      className={cn(`h-12 ${className}`)}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
      ))}
    </TableRow>
  );
}

export function ColumnVisibilityPicker<TData>({ table }: { table: ReactTable<TData> }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='sm'>
          <ColumnsIcon />
          <span>Columns</span>
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        {table
          .getAllColumns()
          .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
          .map((column) => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className='capitalize'
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
